import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { DateRange } from 'react-day-picker';
import type { MessageDB } from '@/types/message';
import type { ThreadDB } from '@/types/thread';

export type ReplyMode = 'reply' | 'reply-all' | 'forward';

type HubContextType = {
  selectedInbox: string | undefined;
  setSelectedInbox: (inbox: string) => void;
  selectedFolderId: string | undefined;
  setSelectedFolderId: (folderId: string | undefined) => void;
  dealThreads: ThreadDB[];
  isDealInbox: boolean;
  myGrantId: string | undefined;
  myGrantEmail: string | undefined;
  verifyGrantId: string | undefined;
  verifyUserId: string | undefined;
  sendAsGrantId: string | undefined;
  setSendAsGrantId: (grantId: string | undefined) => void;
  sendAsEmailAddress: string | undefined;
  setSendAsEmailAddress: (email: string | undefined) => void;
  isTrackedThread: (threadId: string) => boolean;
  replyThread: ThreadDB | undefined;
  replyMessage: MessageDB | undefined;
  replyMode: ReplyMode | undefined;
  setReplyThread: (threadId: string | undefined, message?: MessageDB, mode?: ReplyMode) => void;
  // Used to force re-render when user replies to same message
  replyKey: number;
  incrementReplyKey: () => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  dateRangeFilter: DateRange | undefined;
  setDateRangeFilter: (range: DateRange | undefined) => void;
  clearDateRangeFilter: () => void;
  hasActiveDateFilter: boolean;
};

const HubContext = createContext<HubContextType>({
  selectedInbox: undefined,
  setSelectedInbox: () => {},
  selectedFolderId: undefined,
  setSelectedFolderId: () => {},
  dealThreads: [],
  isDealInbox: false,
  myGrantId: undefined,
  myGrantEmail: undefined,
  verifyGrantId: undefined,
  verifyUserId: undefined,
  sendAsGrantId: undefined,
  setSendAsGrantId: () => {},
  sendAsEmailAddress: undefined,
  setSendAsEmailAddress: () => {},
  isTrackedThread: () => false,
  replyThread: undefined,
  replyMessage: undefined,
  replyMode: undefined,
  setReplyThread: () => {},
  replyKey: 0,
  incrementReplyKey: () => {},
  searchQuery: '',
  setSearchQuery: () => {},
  dateRangeFilter: undefined,
  setDateRangeFilter: () => {},
  clearDateRangeFilter: () => {},
  hasActiveDateFilter: false,
});

type HubProviderProps = {
  children: ReactNode;
  selectedInbox: string | undefined;
  setSelectedInbox: (inbox: string) => void;
  dealThreads: ThreadDB[];
  myGrantId: string | undefined;
  myGrantEmail: string | undefined;
  verifyGrantId: string | undefined;
  verifyUserId: string | undefined;
  selectedFolderId: string | undefined;
  setSelectedFolderId: (folderId: string | undefined) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  dateRangeFilter: DateRange | undefined;
  setDateRangeFilter: (range: DateRange | undefined) => void;
  clearDateRangeFilter: () => void;
  hasActiveDateFilter: boolean;
};

export function HubProvider({
  children,
  selectedInbox,
  setSelectedInbox,
  dealThreads,
  myGrantId,
  myGrantEmail,
  verifyGrantId,
  verifyUserId,
  selectedFolderId,
  setSelectedFolderId,
  searchQuery,
  setSearchQuery,
  dateRangeFilter,
  setDateRangeFilter,
  clearDateRangeFilter,
  hasActiveDateFilter,
}: HubProviderProps) {
  const [sendAsGrantId, setSendAsGrantId] = useState<string | undefined>();
  const [sendAsEmailAddress, setSendAsEmailAddress] = useState<string | undefined>();
  const [replyThread, setReplyThread] = useState<ThreadDB | undefined>();
  const [replyMessage, setReplyMessage] = useState<MessageDB | undefined>();
  const [replyMode, setReplyMode] = useState<ReplyMode | undefined>();
  const [replyKey, setReplyKey] = useState(0);

  // Checks if a thread exists in dealThreads
  const isTrackedThread = useMemo(
    () => (threadId: string): boolean =>
      dealThreads.some((thread) => thread && thread.thread_id === threadId),
    [dealThreads],
  );

  // Checks if the selected inbox is 'deal'
  const isDealInbox = selectedInbox === 'deal';

  // useMemo is used to prevent the value from being recalculated on every render
  const value = useMemo(
    () => ({
      selectedInbox,
      setSelectedInbox,
      selectedFolderId,
      setSelectedFolderId,
      dealThreads,
      isTrackedThread,
      isDealInbox,
      myGrantId,
      myGrantEmail,
      verifyGrantId,
      verifyUserId,
      replyThread,
      replyMessage,
      replyMode,
      sendAsGrantId,
      setSendAsGrantId,
      sendAsEmailAddress,
      setSendAsEmailAddress,
      // Sets the reply thread by finding the matching ThreadDB in dealThreads
      // If threadId is undefined, clears the reply thread
      // If threadId is provided but not found in dealThreads, no thread will be set
      setReplyThread: (threadId: string | undefined, message?: MessageDB, mode?: ReplyMode) => {
        console.log(`setReplyThread(${threadId})${message ? ` (messageId: ${message.message_id})` : ''}${mode ? ` (mode: ${mode})` : ''}`);
        if (!threadId) {
          console.log(`no threadId provided, clearing replyThread${message ? ` (messageId: ${message.id})` : ''}`);
          setReplyThread(undefined);
          setReplyMessage(undefined);
          setReplyMode(undefined);
          return;
        }
        // Find the thread in dealThreads
        const thread = dealThreads.find((t) => t && t.thread_id === threadId);
        if (!thread) {
          console.log(`threadId ${threadId} not found in dealThreads${message ? ` (messageId: ${message.id})` : ''}`);
          return;
        }
        setReplyThread(thread);
        setReplyMessage(message);
        setReplyMode(mode || 'reply-all');
      },
      replyKey,
      incrementReplyKey: () => setReplyKey((prev) => prev + 1),
      searchQuery,
      setSearchQuery,
      dateRangeFilter,
      setDateRangeFilter,
      clearDateRangeFilter,
      hasActiveDateFilter,
    }),
    [
      selectedInbox,
      setSelectedInbox,
      selectedFolderId,
      setSelectedFolderId,
      dealThreads,
      myGrantId,
      myGrantEmail,
      verifyGrantId,
      verifyUserId,
      isTrackedThread,
      isDealInbox,
      replyThread,
      replyMessage,
      replyMode,
      sendAsGrantId,
      sendAsEmailAddress,
      replyKey,
      searchQuery,
      dateRangeFilter,
      hasActiveDateFilter,
      clearDateRangeFilter,
    ],
  );

  return (
    <HubContext.Provider value={value}>
      {children}
    </HubContext.Provider>
  );
}

export function useHub(): HubContextType {
  const context = useContext(HubContext);
  if (context === undefined) {
    throw new Error('useHub must be used within a HubProvider');
  }
  return context;
}
